import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionIcon, Button, Group, Stack, Text } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconDownload, IconFileText, IconSignature } from '@tabler/icons-react';
import { orderService } from '@/api/services/OrderService';
import { getApiMessage, isServiceUnavailable } from '@/api/errors';
import classes from './AgentAssignmentBlock.module.css';

type Status = 'initial' | 'generated' | 'signed' | 'changed';

interface AgentAssignmentBlockProps {
  /** Обязательные данные заполнены и поручение можно сформировать. */
  readonly ready: boolean;
  /** Сформировать поручение по текущим данным → файл поручения. */
  readonly generate: () => Promise<{ readonly id: number }>;
  /** Подписать сформированное поручение. */
  readonly sign: (fileId: number) => Promise<void>;
  /** Подпись значимых данных: если она изменилась после формирования, поручение нужно сформировать заново. */
  readonly signatureKey: string;
  /** Текущий id поручения (источник истины — у родителя). */
  readonly fileId: number | null;
  /** Подписано ли (родитель не даёт отправить, пока false). */
  readonly signed: boolean;
  readonly onChange: (fileId: number | null, signed: boolean) => void;
  /** Пояснение над поручением и подсказка, пока данных не хватает. */
  readonly intro: string;
  readonly needFillFirst: string;
}

// Поручение агенту: формирование → подпись. Изменение значимых данных после формирования аннулирует поручение
// с пояснением «Данные изменились». Ответ формирования, пришедший после такой правки, не принимается: документ
// составлен по прежним данным. После подписи кнопок нет — при правке поручение сбросится само. Общий блок формы
// предложения и встречного предложения покупателю; запросы формирования и подписи передаёт страница.
export default function AgentAssignmentBlock({
  ready,
  generate,
  sign,
  signatureKey,
  fileId,
  signed,
  onChange,
  intro,
  needFillFirst,
}: AgentAssignmentBlockProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>(signed && fileId != null ? 'signed' : 'initial');
  // Подпись данных, по которым сформирован текущий документ, и подпись на момент отправки запроса формирования.
  const keyAtGenerate = useRef<string>(signatureKey);
  const latestKey = useRef<string>(signatureKey);
  latestKey.current = signatureKey;
  const signRef = useRef<HTMLButtonElement>(null);
  // Ответ, пришедший после того как блок скрыли, не применяется: родитель мог за это время сбросить поручение, а
  // новый экземпляр блока принял бы устаревший документ за актуальный.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const fileRef = useRef<HTMLDivElement>(null);

  // Правка значимых данных после формирования или подписи аннулирует поручение.
  useEffect(() => {
    if ((status === 'generated' || status === 'signed') && signatureKey !== keyAtGenerate.current) {
      setStatus('changed');
      onChange(null, false);
    }
  }, [signatureKey, status, onChange]);

  const onError = (e: unknown): void => {
    notifications.show({
      color: 'red',
      message: isServiceUnavailable(e) ? t('auth.errors.unavailable') : (getApiMessage(e) ?? t('auth.errors.generic')),
    });
  };

  const generateMutation = useMutation({
    // Подпись данных запоминается в момент нажатия: запрос может начаться позже, уже после правки.
    mutationFn: async (key: string) => {
      const file = await generate();
      return { file, key };
    },
    onSuccess: ({ file, key }) => {
      if (!mounted.current) {
        return;
      }
      if (key !== latestKey.current) {
        // Данные изменились, пока документ формировался: он составлен по прежним данным.
        setStatus('changed');
        onChange(null, false);
        return;
      }
      keyAtGenerate.current = key;
      setStatus('generated');
      onChange(file.id, false);
      notifications.show({ color: 'lime', message: t('app.sell.form.agent.generated') });
      // Кнопка формирования неактивна во время запроса и теряет фокус: переводим его на следующий шаг.
      requestAnimationFrame(() => signRef.current?.focus());
    },
    onError,
  });

  const signMutation = useMutation({
    mutationFn: (id: number) => sign(id),
    onSuccess: (_, id) => {
      if (!mounted.current || latestKey.current !== keyAtGenerate.current) {
        // Данные изменились во время подписи: поручение уже аннулировано, подписанный документ устарел.
        return;
      }
      setStatus('signed');
      onChange(id, true);
      notifications.show({ color: 'lime', message: t('app.sell.form.agent.signed') });
      // Кнопка «Подписать» исчезает: фокус — на строку подписанного поручения.
      requestAnimationFrame(() => fileRef.current?.focus());
    },
    onError,
  });

  const download = useMutation({
    mutationFn: () => orderService.downloadOrderFile(fileId!),
    onError,
  });

  const showFile = (status === 'generated' || status === 'signed') && fileId != null;
  const busy = generateMutation.isPending || signMutation.isPending;

  // Без рамки: раздел с заголовком даёт страница.
  return (
    <Stack gap="md">
      <Text size="sm" c="gray.7">
        {intro}
      </Text>

      {/* Состояние поручения — живая область, смонтированная всегда, чтобы смена состояния была объявлена.
          Файл — строка между линиями: значок, состояние текстом и скачивание. «Подписано» зелёным, «Данные
          изменились» красным, без плашек. */}
      <div role="status">
        {showFile ? (
          <Group justify="space-between" wrap="nowrap" gap="sm" className={classes.file} ref={fileRef} tabIndex={-1}>
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
              <IconFileText size={18} color="var(--mantine-color-brand-7)" style={{ flexShrink: 0 }} aria-hidden />
              <Text size="sm" fw={600} c={status === 'signed' ? 'brand.7' : undefined}>
                {status === 'signed' ? t('app.sell.form.agent.signedBadge') : t('app.sell.form.agent.readyBadge')}
              </Text>
            </Group>
            <ActionIcon
              variant="default"
              size={34}
              aria-label={t('app.sell.form.agent.download')}
              loading={download.isPending}
              onClick={() => download.mutate()}
            >
              <IconDownload size={18} />
            </ActionIcon>
          </Group>
        ) : status === 'changed' ? (
          <Text size="sm" fw={600} c="loss.8">
            {t('app.sell.form.agent.changedBadge')}
          </Text>
        ) : null}
      </div>

      {status !== 'signed' ? (
        <Group gap="sm" wrap="wrap">
          <Button
            variant="default"
            leftSection={<IconFileText size={16} aria-hidden />}
            loading={generateMutation.isPending}
            disabled={!ready || busy}
            onClick={() => generateMutation.mutate(latestKey.current)}
          >
            {status === 'initial' ? t('app.sell.form.agent.generate') : t('app.sell.form.agent.regenerate')}
          </Button>
          {status === 'generated' && fileId != null ? (
            <Button
              ref={signRef}
              leftSection={<IconSignature size={16} aria-hidden />}
              loading={signMutation.isPending}
              disabled={generateMutation.isPending}
              onClick={() => signMutation.mutate(fileId)}
            >
              {t('app.sell.form.agent.sign')}
            </Button>
          ) : null}
        </Group>
      ) : null}

      {!ready && !busy && status !== 'signed' ? (
        <Text size="sm" c="gray.7">
          {needFillFirst}
        </Text>
      ) : null}
    </Stack>
  );
}
